import { Controller } from '@hotwired/stimulus';

// Controller for drag & drop file upload in the contractor portal
// Manages three states: placeholder, preview, and error
// Also handles duplicate file detection via checksum
export default class extends Controller {
  static targets = [
    'dropzone',
    'input',
    'placeholder',
    'preview',
    'error',
    'filename',
    'filesize',
    'errorMessage',
    'submit',
    'customName',
    'duplicateModal',
    'duplicateDate',
    'rateLimitModal',
    'rateLimitRetryAfter',
    'previewModal',
    'previewModalFilename',
    'previewModalFilesize',
    'previewPdf',
    'previewImage',
    'previewImageContainer',
  ];
  static values = { maxSize: Number, checkDuplicateUrl: String };

  currentFile = null;

  connect() {
    this.maxSizeValue = this.maxSizeValue || 10485760; // 10MB default

    // Prevent default browser drag behavior
    this.element.addEventListener('dragover', this.preventDefault.bind(this));
    this.element.addEventListener('drop', this.preventDefault.bind(this));

    // Add ESC key support for modals
    this.handleKeydown = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnect() {
    // Clean up event listener
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      // Close preview modal if open
      if (this.hasPreviewModalTarget && !this.previewModalTarget.classList.contains('hidden')) {
        this.closePreviewModal();
      }
      // Close duplicate modal if open
      if (this.hasDuplicateModalTarget && !this.duplicateModalTarget.classList.contains('hidden')) {
        this.closeDuplicateModal();
      }
      // Close rate limit modal if open
      if (this.isRateLimitModalOpen()) {
        this.closeRateLimitModal();
      }
    }
  }

  preventDefault(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  dragover(event) {
    this.preventDefault(event);
    this.dropzoneTarget.classList.add('border-primary-500', 'bg-primary-50');
    this.dropzoneTarget.classList.remove('border-neutral-300');
  }

  dragleave(event) {
    this.preventDefault(event);
    this.dropzoneTarget.classList.remove('border-primary-500', 'bg-primary-50');
    this.dropzoneTarget.classList.add('border-neutral-300');
  }

  drop(event) {
    this.preventDefault(event);
    this.dragleave(event);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      this.inputTarget.files = files;
      this.handleFile(files[0]);
    }
  }

  change(event) {
    const file = event.target.files[0];
    if (file) this.handleFile(file);
  }

  validateBeforeSubmit(event) {
    // Prevent form submission if:
    // 1. No file is selected
    // 2. Submit button is disabled (meaning there's an error or no valid file)
    if (!this.inputTarget.files || this.inputTarget.files.length === 0) {
      event.preventDefault();
      this.showError('Veuillez sélectionner un fichier');
      return false;
    }

    if (this.hasSubmitTarget && this.submitTarget.disabled) {
      event.preventDefault();
      return false;
    }

    return true;
  }

  handleBeforeFetchResponse(event) {
    const fetchResponse = event.detail?.fetchResponse;
    const statusCode = fetchResponse?.statusCode || fetchResponse?.response?.status;
    if (statusCode !== 429) {
      return;
    }

    event.preventDefault();
    const retryAfter = this.extractRetryAfter(fetchResponse?.response?.headers);
    this.showRateLimitModal(retryAfter);
  }

  async handleFile(file) {
    // Reset states first
    this.hideAll();

    // Validate file size
    if (file.size > this.maxSizeValue) {
      this.showError(`Fichier trop volumineux (${this.formatSize(file.size)})`);
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      this.showError('Format non supporté. Utilisez PDF, JPEG ou PNG.');
      return;
    }

    // Check for duplicates if URL is configured
    if (this.hasCheckDuplicateUrlValue && this.checkDuplicateUrlValue) {
      const checksum = await this.computeChecksum(file);
      const isDuplicate = await this.checkDuplicate(checksum);
      if (isDuplicate) {
        return; // Modal is shown, don't proceed
      }
    }

    // Store the current file for preview
    this.currentFile = file;

    // Show preview
    this.showPreview(file);
  }

  async computeChecksum(file) {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  async checkDuplicate(checksum) {
    try {
      const response = await fetch(this.checkDuplicateUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({ checksum }),
      });

      if (response.status === 429) {
        this.showRateLimitModal(this.extractRetryAfter(response.headers));
        return true;
      }

      if (!response.ok) {
        throw new Error(`Duplicate check failed with status ${response.status}`);
      }

      const data = await response.json();

      if (data.duplicate) {
        this.showDuplicateModal(data.uploaded_at);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      // Continue with upload if check fails
      return false;
    }
  }

  showDuplicateModal(uploadedAt) {
    if (this.hasDuplicateModalTarget) {
      // Update the date in the modal
      if (this.hasDuplicateDateTarget) {
        this.duplicateDateTarget.textContent = uploadedAt;
      }
      this.duplicateModalTarget.classList.remove('hidden');
    }

    // Clear the input and current file
    this.inputTarget.value = '';
    this.currentFile = null;
  }

  closeDuplicateModal(event) {
    if (event) {
      event.preventDefault();
    }
    if (this.hasDuplicateModalTarget) {
      this.duplicateModalTarget.classList.add('hidden');
    }
    // Reset to placeholder state
    this.clear();
  }

  showRateLimitModal(retryAfterSeconds = null) {
    const retryAfterText =
      Number.isInteger(retryAfterSeconds) && retryAfterSeconds > 0
        ? `${retryAfterSeconds} secondes`
        : 'quelques instants';

    if (this.hasRateLimitRetryAfterTarget) {
      this.rateLimitRetryAfterTarget.textContent = retryAfterText;
    }

    const modalElement = this.rateLimitModalElement();
    if (!modalElement) {
      return;
    }

    if (modalElement.modalController) {
      modalElement.modalController.open();
      return;
    }

    modalElement.classList.remove('hidden');
    document.body.classList.add('overflow-hidden');
  }

  closeRateLimitModal(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const modalElement = this.rateLimitModalElement();
    if (!modalElement) {
      return;
    }

    if (modalElement.modalController) {
      modalElement.modalController.close();
      return;
    }

    modalElement.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
  }

  showPreview(file) {
    this.placeholderTarget.classList.add('hidden');
    if (this.hasErrorTarget) this.errorTarget.classList.add('hidden');
    this.previewTarget.classList.remove('hidden');

    this.filenameTarget.textContent = file.name;
    this.filesizeTarget.textContent = this.formatSize(file.size);

    // Auto-fill custom name with filename (without extension)
    if (this.hasCustomNameTarget && !this.customNameTarget.value) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      this.customNameTarget.value = nameWithoutExt;
    }

    // Enable submit button
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false;
    }

    // Success border styling
    this.dropzoneTarget.classList.add('border-success-500', 'bg-success-50/50');
    this.dropzoneTarget.classList.remove('border-neutral-300', 'border-dashed');
  }

  showError(message) {
    this.placeholderTarget.classList.add('hidden');
    this.previewTarget.classList.add('hidden');
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove('hidden');
      if (this.hasErrorMessageTarget) {
        this.errorMessageTarget.textContent = message;
      }
    }

    // Disable submit button
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true;
    }

    // Clear the input value
    this.inputTarget.value = '';
    // Also clear the files property for safety
    if (this.inputTarget.files && this.inputTarget.files.length > 0) {
      const dt = new DataTransfer();
      this.inputTarget.files = dt.files;
    }

    // Error border styling
    this.dropzoneTarget.classList.add('border-danger-500', 'bg-danger-50/50');
    this.dropzoneTarget.classList.remove('border-neutral-300');
  }

  showPreviewModal(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.currentFile || !this.hasPreviewModalTarget) {
      return;
    }

    // Store currently focused element to restore later
    this.previouslyFocusedElement = document.activeElement;

    // Update modal header with file info
    if (this.hasPreviewModalFilenameTarget) {
      this.previewModalFilenameTarget.textContent = this.currentFile.name;
    }
    if (this.hasPreviewModalFilesizeTarget) {
      this.previewModalFilesizeTarget.textContent = this.formatSize(this.currentFile.size);
    }

    // Create object URL for the file
    const fileUrl = URL.createObjectURL(this.currentFile);

    // Show appropriate preview based on file type
    if (this.currentFile.type === 'application/pdf') {
      // Show PDF in iframe
      if (this.hasPreviewPdfTarget) {
        this.previewPdfTarget.src = fileUrl;
        this.previewPdfTarget.classList.remove('hidden');
      }
      if (this.hasPreviewImageContainerTarget) {
        this.previewImageContainerTarget.classList.add('hidden');
      }
    } else if (this.currentFile.type.startsWith('image/')) {
      // Show image
      if (this.hasPreviewImageTarget && this.hasPreviewImageContainerTarget) {
        this.previewImageTarget.src = fileUrl;
        this.previewImageContainerTarget.classList.remove('hidden');
      }
      if (this.hasPreviewPdfTarget) {
        this.previewPdfTarget.classList.add('hidden');
      }
    }

    // Show modal
    this.previewModalTarget.classList.remove('hidden');

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Focus on close button for keyboard navigation
    const closeButton = this.previewModalTarget.querySelector('button[aria-label="Fermer"]');
    if (closeButton) {
      closeButton.focus();
    }
  }

  closePreviewModal(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.hasPreviewModalTarget) {
      return;
    }

    // Hide modal
    this.previewModalTarget.classList.add('hidden');

    // Clean up object URLs to prevent memory leaks
    if (this.hasPreviewPdfTarget && this.previewPdfTarget.src) {
      URL.revokeObjectURL(this.previewPdfTarget.src);
      this.previewPdfTarget.src = '';
      this.previewPdfTarget.classList.add('hidden');
    }
    if (this.hasPreviewImageTarget && this.previewImageTarget.src) {
      URL.revokeObjectURL(this.previewImageTarget.src);
      this.previewImageTarget.src = '';
    }
    if (this.hasPreviewImageContainerTarget) {
      this.previewImageContainerTarget.classList.add('hidden');
    }

    // Restore body scroll
    document.body.style.overflow = '';

    // Restore focus to previously focused element
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
      this.previouslyFocusedElement = null;
    }
  }

  clear(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.inputTarget.value = '';
    this.currentFile = null;
    this.hideAll();
    this.placeholderTarget.classList.remove('hidden');

    // Disable submit button
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true;
    }

    // Reset border styling
    this.dropzoneTarget.classList.remove(
      'border-success-500',
      'bg-success-50/50',
      'border-danger-500',
      'bg-danger-50/50'
    );
    this.dropzoneTarget.classList.add('border-neutral-300', 'border-dashed');
  }

  hideAll() {
    this.placeholderTarget.classList.add('hidden');
    this.previewTarget.classList.add('hidden');
    if (this.hasErrorTarget) this.errorTarget.classList.add('hidden');
  }

  extractRetryAfter(headers) {
    if (!headers || typeof headers.get !== 'function') {
      return null;
    }

    const retryAfterRaw = headers.get('Retry-After');
    const retryAfter = Number.parseInt(retryAfterRaw, 10);
    return Number.isInteger(retryAfter) && retryAfter > 0 ? retryAfter : null;
  }

  rateLimitModalElement() {
    if (!this.hasRateLimitModalTarget) {
      return null;
    }

    return this.rateLimitModalTarget.closest('[data-controller~="modal"]');
  }

  isRateLimitModalOpen() {
    const modalElement = this.rateLimitModalElement();
    return modalElement && !modalElement.classList.contains('hidden');
  }

  formatSize(bytes) {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / 1048576).toFixed(1)} Mo`;
  }
}
